import { NavLink } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { getConfig } from "../../config";
import { useAuth } from "../../auth/AuthProvider";
import { useSessionStore } from "../../store/sessionStore";
import { useUiStore } from "../../store/uiStore";
import { RoleBadge } from "../Pills";
import { IconButton } from "../IconButton";
import { Button } from "../Button";
import { reportError } from "../../services/eventBus";

interface RibbonLink {
  to: string;
  label: string;
}

const PRIMARY_LINKS: readonly RibbonLink[] = [
  { to: "/uw/recent", label: "Recent Groups" },
  { to: "/uw/search", label: "Search for Group" },
  { to: "/uw/groups/new", label: "Add Group" },
];

const ACTUARIAL_LINKS: readonly RibbonLink[] = [
  { to: "/uw/actuarial/expected-debits", label: "Expected Debit Tables" },
  { to: "/uw/actuarial/conditions", label: "Customize Conditions" },
  { to: "/uw/actuarial/mugs-manual-upload", label: "MUGS Manual Upload" },
];

function linkClass(isActive: boolean): string {
  return `inline-flex min-h-[44px] items-center whitespace-nowrap border-b-2 px-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded-sm ${isActive ? "border-brand-green text-brand-navy" : "border-transparent text-gray-600 hover:text-brand-navy"}`;
}

/** Fixed top ribbon with role-gated links, role badge, user name, and Sign Out. */
export function AppRibbon() {
  const role = useSessionStore((state) => state.role);
  const user = useSessionStore((state) => state.user);
  const clearSession = useSessionStore((state) => state.clear);
  const setShortcutHelpOpen = useUiStore((state) => state.setShortcutHelpOpen);
  const { user: signedIn, logout } = useAuth();
  const displayName = user?.displayName ?? signedIn?.profile.name ?? "";

  const handleSignOut = async () => {
    try {
      clearSession();
      if (getConfig().AUTH_ENABLED) await logout();
      else useUiStore.getState().addToast("Sign out is simulated while authentication is disabled.", "info");
    } catch (error) {
      reportError(error, "AppRibbon.signOut");
    }
  };

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-gray-200 bg-white">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-brand-navy focus:px-3 focus:py-2 focus:text-white">
        Skip to main content
      </a>
      <nav aria-label="Primary" className="flex h-16 items-center gap-4 px-6">
        <NavLink to="/uw/recent" title="UW Workbench home" className="flex shrink-0 items-center focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded">
          <img src="/horizontal-optimyl-logo-black-text.svg" alt="Optimyl UW Workbench" className="h-7" />
        </NavLink>
        <div className="flex flex-1 items-center gap-3 overflow-x-auto">
          {PRIMARY_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={({ isActive }) => linkClass(isActive)}>
              {link.label}
            </NavLink>
          ))}
          {role === "Actuarial" && (
            <>
              <span aria-hidden="true" className="h-6 w-px bg-gray-200" />
              {ACTUARIAL_LINKS.map((link) => (
                <NavLink key={link.to} to={link.to} className={({ isActive }) => linkClass(isActive)}>
                  {link.label}
                </NavLink>
              ))}
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <IconButton icon={HelpCircle} label="Keyboard shortcuts" onClick={() => setShortcutHelpOpen(true)} />
          <RoleBadge role={role} />
          {displayName && <span className="whitespace-nowrap text-sm text-gray-700">{displayName}</span>}
          <Button variant="ghost" size="sm" onClick={() => void handleSignOut()}>
            Sign Out
          </Button>
        </div>
      </nav>
    </header>
  );
}
